import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PORTFOLIO } from "../data/content";

export default function Portfolio() {
  const [active, setActive] = useState(null);
  return (
    <section id="portfolio" className="vw-section relative bg-[var(--vw-bg-soft)]" data-testid="vw-portfolio">
      <div className="vw-container">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="vw-eyebrow mb-6">DELIVERED WORK · 2015 — 2024</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight max-w-[14ch]">
              A shelf we're happy to <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">walk you through</span>.
            </h2>
          </div>
          <p className="max-w-sm text-[var(--vw-text-dim)] leading-relaxed">
            Features, series, and short-form work — delivered to broadcasters, streamers, and international clients across four continents.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PORTFOLIO.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              onClick={() => setActive(p)}
              className="group relative aspect-[4/5] overflow-hidden border border-[var(--vw-border)] hover:border-[var(--vw-cyan)] bg-black text-left transition-colors"
              data-testid={`vw-portfolio-${p.id}`}
            >
              <img
                src={p.thumb}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

              {/* Tag */}
              <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border border-[var(--vw-cyan)] px-2.5 py-1 bg-black/40">
                <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)]" />
                {p.tag}
              </div>

              {/* Runtime */}
              <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] text-white/80">
                {p.runtime}
              </div>

              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2">
                  {p.format} · {p.year}
                </div>
                <div className="font-display text-3xl md:text-4xl leading-[0.95] tracking-tight text-white mb-3">
                  {p.title}
                </div>
                <div className="max-h-0 group-hover:max-h-32 overflow-hidden transition-all duration-500 opacity-0 group-hover:opacity-100">
                  <p className="text-sm text-white/85 leading-relaxed line-clamp-3">
                    {p.synopsis}
                  </p>
                  <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]">
                    View Case Study
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <CaseModal item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}

function CaseModal({ item, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="vw-case-modal"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-lg" onClick={onClose} />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-[var(--vw-surface)] border border-[var(--vw-border-strong)]"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center bg-black/40 border border-[var(--vw-border-strong)] hover:border-[var(--vw-cyan)] hover:text-[var(--vw-cyan)] transition-colors"
          data-testid="vw-case-close"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18L18 6"/></svg>
        </button>

        {item.vimeoId ? (
          <div className="vw-widescreen w-full bg-black">
            <iframe
              src={`https://player.vimeo.com/video/${item.vimeoId}?title=0&byline=0&portrait=0`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={item.title}
            />
          </div>
        ) : (
          <div className="vw-widescreen w-full bg-black relative">
            <img src={item.thumb} alt={item.title} className="w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 grid place-items-center font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-text-dim)]">
              Reel Available on Request · NDA Protected
            </div>
          </div>
        )}

        <div className="p-8 md:p-12">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border border-[var(--vw-cyan)] px-2 py-1">{item.tag}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">{item.format}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">· {item.year}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">· {item.runtime}</span>
          </div>
          <h3 className="font-display text-5xl md:text-6xl leading-[0.92] tracking-tight mb-6">{item.title}</h3>
          <p className="text-lg text-[var(--vw-text-dim)] leading-relaxed max-w-3xl">{item.synopsis}</p>
          <div className="mt-8 border-t border-[var(--vw-border)] pt-6 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.28em]">
            <div><span className="text-[var(--vw-text-mute)]">ROLE · </span><span className="text-[var(--vw-text)]">{item.role}</span></div>
            <div><span className="text-[var(--vw-text-mute)]">YEAR · </span><span className="text-[var(--vw-text)]">{item.year}</span></div>
            <div><span className="text-[var(--vw-text-mute)]">FORMAT · </span><span className="text-[var(--vw-text)]">{item.format}</span></div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
